'use client';

import { useState } from 'react';

import R2Image from '@/components/ui/R2Image';

import RichText from '@/components/ui/RichText/RichText';

import type { AccordionAndImgData, AccordionItem } from './types';

interface Props {
  data: AccordionAndImgData;
}

const AccordionAndImgWithBackground = ({ data }: Props) => {
  const { heading, description, accordionContent } = data;
  const { heroSectionBg, heroSectionBgMobile } = data as any;
  const [activeIndex, setActiveIndex] = useState(0);

  if (!accordionContent.length) return null;

  const activeItem: AccordionItem = accordionContent[activeIndex];

  return (
    <section className="relative w-full overflow-hidden bg-linear-to-br py-8 md:py-10">
      {heroSectionBg && (
        <R2Image
          path={heroSectionBg}
          alt=""
          sizes="100vw"
          containerClassName="absolute inset-0 z-0"
          className="object-cover"
          aria-hidden="true"
        />
      )}
      {heroSectionBgMobile && (
        <R2Image
          path={heroSectionBgMobile}
          alt=""
          sizes="100vw"
          containerClassName="absolute inset-0 z-0 md:hidden"
          className="object-cover"
          aria-hidden="true"
        />
      )}
      <div className="content-wrapper relative z-10">
        {/* Section heading */}
        <div className="mb-12 text-center">
          <RichText styles={heading.styles}>{heading.content}</RichText>
          <RichText styles={description.styles}>{description.content}</RichText>
        </div>

        {/* Accordion + Image */}
        <div className="flex flex-col items-center gap-10 lg:flex-row">
          {/* Accordion list */}
          <div className="flex w-full flex-col gap-4 lg:w-1/2">
            {accordionContent.map((item, index) => {
              const isOpen = activeIndex === index;
              return (
                <div key={index} className="overflow-hidden rounded-xl">
                  <button
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className={`text-theme-primary flex w-full items-center justify-between bg-white px-6 py-4 text-left transition-colors`}
                  >
                    <RichText
                      styles={{
                        p: `font-bold text-base ${isOpen ? 'text-theme-primary' : 'text-white'}`,
                        ...item.heading.styles,
                      }}
                    >
                      {item.heading.content}
                    </RichText>
                    <span
                      className={`ml-4 shrink-0 cursor-pointer transition-transform duration-300 ${
                        isOpen
                          ? 'text-theme-primary rotate-180'
                          : 'text-theme-primary'
                      }`}
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ${
                      isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="min-h-0 overflow-hidden bg-white px-6">
                      <div className="pb-4">
                        <RichText styles={item.description.styles}>
                          {item.description.content}
                        </RichText>
                      </div>
                      {/* Image shown inline on mobile */}
                      <div className="relative mb-4 aspect-4/3 w-full overflow-hidden rounded-xl lg:hidden">
                        <R2Image
                          path={item.image}
                          alt={item.heading.content}
                          sizes="(max-width: 1023px) 100vw, 50vw"
                          className="object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Image panel — desktop only */}
          <div className="hidden w-full lg:block lg:w-1/2">
            <div className="relative aspect-4/3 w-full overflow-hidden rounded-2xl">
              <R2Image
                path={activeItem.image}
                alt={activeItem.heading.content}
                sizes="(max-width: 1023px) 100vw, 50vw"
                className="object-cover transition-opacity duration-500"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AccordionAndImgWithBackground;
